import React, { useEffect, useState } from "react";
import client, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2, Star, RefreshCw, Check, X } from "lucide-react";

function FeatureEditor({ features, onChange }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const t = draft.trim();
    if (t) { onChange([...features, t]); setDraft(""); }
  };
  return (
    <div>
      <div className="flex gap-2 mb-2">
        <Input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder="Add feature..."
          className="h-8 text-sm"
        />
        <Button type="button" size="sm" variant="outline" onClick={add} className="h-8 px-2"><Plus size={14} /></Button>
      </div>
      <div className="flex flex-wrap gap-1">
        {features.map((f, i) => (
          <span key={i} className="flex items-center gap-1 px-2 py-0.5 bg-[#006080]/10 text-[#006080] rounded text-xs">
            {f}
            <button onClick={() => onChange(features.filter((_, j) => j !== i))} className="hover:text-red-500"><X size={10} /></button>
          </span>
        ))}
      </div>
    </div>
  );
}

const EMPTY_PLAN = { name: "", description: "", price_monthly: 0, features: [], is_free: false, is_popular: false, cta_label: "Get Started", order: 99, is_active: true, stripe_price_id: "" };

export default function PricingPlansPage() {
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null); // plan being edited (or EMPTY_PLAN for new)
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = () => client.get("/plans/admin").then(r => setPlans(r.data)).catch(() => {});

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ ...EMPTY_PLAN }); setError(""); setSuccess(""); };
  const openEdit = (p) => { setEditing({ ...p }); setError(""); setSuccess(""); };
  const closeEdit = () => setEditing(null);

  const save = async () => {
    setSaving(true); setError(""); setSuccess("");
    try {
      if (editing._id) {
        await client.put(`/plans/admin/${editing._id}`, editing);
      } else {
        await client.post("/plans/admin", editing);
      }
      setSuccess("Plan saved.");
      load();
      closeEdit();
    } catch (e) { setError(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const deletePlan = async (id) => {
    if (!window.confirm("Delete this plan?")) return;
    try { await client.delete(`/plans/admin/${id}`); load(); }
    catch (e) { setError(formatApiError(e)); }
  };

  const seedDefaults = async () => {
    if (!window.confirm("This will RESET all plans to defaults. Continue?")) return;
    try { await client.post("/plans/admin/seed"); setSuccess("Default plans restored."); load(); }
    catch (e) { setError(formatApiError(e)); }
  };

  const setF = (k, v) => setEditing(f => ({ ...f, [k]: v }));

  return (
    <div className="p-6 max-w-5xl mx-auto fade-in">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pricing Plans</h1>
          <p className="text-gray-500 text-sm mt-0.5">Manage plans shown on the landing page</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={seedDefaults} className="gap-1"><RefreshCw size={14} />Restore Defaults</Button>
          <Button size="sm" onClick={openNew} className="bg-[#006080] hover:bg-[#004d66] text-white gap-1" data-testid="new-plan-btn"><Plus size={14} />New Plan</Button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
      {success && <div className="mb-4 p-3 rounded bg-green-50 border border-green-200 text-green-700 text-sm flex items-center gap-2"><Check size={14} />{success}</div>}

      {/* Plans grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {plans.map(p => (
          <div key={p._id} className={`bg-white border-2 rounded-xl p-5 relative ${p.is_popular ? "border-[#006080]" : "border-gray-200"} ${!p.is_active ? "opacity-50" : ""}`}>
            {p.is_popular && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#006080] text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                <Star size={10} fill="white" />Most Popular
              </div>
            )}
            <div className="mb-3">
              <h3 className="font-bold text-gray-900 text-lg">{p.name}</h3>
              <p className="text-sm text-gray-500 mt-0.5">{p.description}</p>
            </div>
            <div className="text-3xl font-black text-gray-900 mb-4">
              {p.is_free ? <span className="text-[#006080]">Free</span> : <><span className="text-[#006080]">${p.price_monthly}</span><span className="text-sm font-normal text-gray-400">/mo</span></>}
            </div>
            <ul className="space-y-1 mb-4">
              {p.features.slice(0, 5).map((f, i) => (
                <li key={i} className="text-xs text-gray-600 flex items-center gap-1.5">
                  <Check size={11} className="text-[#006080] shrink-0" />{f}
                </li>
              ))}
              {p.features.length > 5 && <li className="text-xs text-gray-400">+{p.features.length - 5} more...</li>}
            </ul>
            <div className="flex gap-2 mt-auto">
              <Button size="sm" variant="outline" onClick={() => openEdit(p)} className="flex-1 h-7 text-xs gap-1"><Pencil size={11} />Edit</Button>
              <Button size="sm" variant="outline" onClick={() => deletePlan(p._id)} className="h-7 text-xs text-red-500 hover:bg-red-50" data-testid={`delete-plan-${p._id}`}><Trash2 size={11} /></Button>
            </div>
          </div>
        ))}
        {plans.length === 0 && (
          <div className="col-span-3 py-16 text-center text-gray-400">
            <p className="text-sm">No plans yet. <button onClick={openNew} className="text-[#006080] underline">Create one</button> or <button onClick={seedDefaults} className="text-[#006080] underline">restore defaults</button>.</p>
          </div>
        )}
      </div>

      {/* Edit drawer */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="font-bold text-gray-900">{editing._id ? "Edit Plan" : "New Plan"}</h2>
              <button onClick={closeEdit}><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              {error && <div className="p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-500">Plan Name *</Label>
                  <Input className="mt-1" value={editing.name} onChange={e => setF("name", e.target.value)} placeholder="Starter" data-testid="plan-name-input" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Monthly Price (USD)</Label>
                  <Input className="mt-1" type="number" min={0} step={0.01} value={editing.price_monthly} onChange={e => setF("price_monthly", parseFloat(e.target.value) || 0)} />
                </div>
              </div>

              <div>
                <Label className="text-xs text-gray-500">Description</Label>
                <Input className="mt-1" value={editing.description} onChange={e => setF("description", e.target.value)} />
              </div>

              <div>
                <Label className="text-xs text-gray-500">CTA Button Label</Label>
                <Input className="mt-1" value={editing.cta_label} onChange={e => setF("cta_label", e.target.value)} placeholder="Get Started" />
              </div>

              <div>
                <Label className="text-xs text-gray-500">Stripe Price ID (optional)</Label>
                <Input className="mt-1 font-mono text-sm" value={editing.stripe_price_id} onChange={e => setF("stripe_price_id", e.target.value)} placeholder="price_..." />
                <p className="text-xs text-gray-400 mt-0.5">Leave blank to use plan amount directly</p>
              </div>

              <div>
                <Label className="text-xs text-gray-500">Display Order</Label>
                <Input className="mt-1 w-24" type="number" min={0} value={editing.order} onChange={e => setF("order", parseInt(e.target.value) || 0)} />
              </div>

              <div>
                <Label className="text-xs text-gray-500 mb-2 block">Features</Label>
                <FeatureEditor features={editing.features} onChange={v => setF("features", v)} />
              </div>

              <div className="flex gap-6">
                <div className="flex items-center gap-2">
                  <Switch checked={editing.is_free} onCheckedChange={v => setF("is_free", v)} id="is_free" />
                  <Label htmlFor="is_free" className="text-xs text-gray-600">Free plan</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={editing.is_popular} onCheckedChange={v => setF("is_popular", v)} id="is_popular" />
                  <Label htmlFor="is_popular" className="text-xs text-gray-600">Mark as popular</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={editing.is_active} onCheckedChange={v => setF("is_active", v)} id="is_active" />
                  <Label htmlFor="is_active" className="text-xs text-gray-600">Active</Label>
                </div>
              </div>
            </div>
            <div className="flex gap-2 justify-end p-5 border-t">
              <Button variant="outline" onClick={closeEdit}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-[#006080] hover:bg-[#004d66] text-white" data-testid="save-plan-btn">
                {saving ? "Saving..." : "Save Plan"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
